import { AsyncPipe, DatePipe, formatDate } from '@angular/common';
import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Store } from '@ngrx/store';
import { COURSE_TAB, TabMenuComponent } from '@shared';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { LiveSessionService } from 'projects/thkee-common/src/lib/services/live-session/live-session.service';
import { TabItem } from '@shared/models/tabs-constant';
import {
  HttpService,
  LoaderService,
  ProcessingDirective,
  selectActivityStateV2,
  selectCourseV2,
  Series,
  Session,
} from 'thkee-common';
import { CourseHeaderComponent } from '../components/course-header/course-header.component';
import { CourseHeadingComponent } from '../components/course-heading/course-heading.component';

@Component({
  selector: 'app-course-schedule',
  imports: [
    DatePipe,
    CourseHeaderComponent,
    CourseHeadingComponent,
    AngularSvgIconModule,
    ProcessingDirective,
    TabMenuComponent,
    AsyncPipe,
  ],
  templateUrl: './course-schedule.component.html',
  styleUrl: './course-schedule.component.scss',
})
export class CourseScheduleComponent {
  private readonly store = inject(Store);
  private readonly liveSessionService = inject(LiveSessionService);
  private readonly http = inject(HttpService);
  course$ = this.store.select(selectCourseV2);

  // ────────── Signals ──────────
  // convert observable (from store or http) into signals
  readonly course = toSignal(this.course$, { initialValue: null }); // convert observable from store into signal
  readonly auth = toSignal(this.http.get<{ timezone?: string }>('/api/v2/users/profile/authentication/'), { initialValue: null }); // convert observable from HTTP into signal
  // signals
  readonly projectId = signal<string | null>(null);
  readonly sessions = signal<Session[]>([]);
  readonly series = signal<Series[]>([]);
  readonly timeZone = signal('Asia/Riyadh');
  readonly isLoading = signal(true);

  // ────────── Derived / Computed ──────────
  readonly courseType = computed(() => this.course()?.course_mode ?? null);

  readonly hasData = computed(() => {
    switch (this.courseType()) {
      case 'freestyle':
      case 'single_session':
        return this.sessions().length > 0;
      case 'multiple_session':
        return this.series().length > 0;
      default:
        return false;
    }
  });

  readonly hasNoData = computed(() => !this.isLoading() && !this.hasData());

  // ────────── Auth → ProjectId sequence ──────────
  readonly authEffect = effect(() => {
    const a = this.auth();
    if (a?.timezone) {
      this.timeZone.set(a.timezone);
    }
  });

  readonly projectIdEffect = effect(() => {
    const course = this.course();
    if (course?.project) {
      this.projectId.set(course.project);
    }
  });

  // ────────── Load sessions / series ──────────
  readonly loadDataEffect = effect(() => {
    const course = this.course();
    const pid = this.projectId();

    if (!course || !pid) return;

    this.isLoading.set(true);

    // Use unified endpoint for both single and multiple session modes
    this.liveSessionService.getProjectSchedule(pid, {}).subscribe({
      next: (resp: any) => {
        const seriesResults = (resp?.results ?? []) as Series[];
        if (course.course_mode === 'single_session' || course.course_mode === 'freestyle') {
          // For single_session/freestyle: flatten sessions from all series
          const flatSessions = seriesResults.flatMap((s: Series) => s.sessions || []);
          this.sessions.set(flatSessions);
          this.series.set([]);
        } else {
          // For multiple_session: use series directly
          this.series.set(seriesResults);
          this.sessions.set([]);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.sessions.set([]);
        this.series.set([]);
        this.isLoading.set(false);
      },
    });
  });

  // ────────── Helpers ──────────
  getSeriesLetter(index: number): string {
    return String.fromCharCode(65 + index); // 0 => A, 1 => B, ...
  }

  // Helper: get day names for series sessions
  getAvailableDays(series: Series): string {
    if (!series.sessions || series.sessions.length === 0) return '—';

    const days = Array.from(
      new Set(
        series.sessions.map((s: Session) =>
          formatDate(s.start_datetime, 'EEEE', 'en', this.timeZone())
        )
      )
    );

    if (days.length <= 2) return days.join(', ');

    return `${days.slice(0, 2).join(', ')} and ${days.length - 2} more`;
  }

  // header related
  private loaderService = inject(LoaderService);
  readonly isDefaultView = input<boolean>(true);
  courseState: string = '0';
  readOnly: boolean = true;
  tabItems: TabItem[] = COURSE_TAB;
  activityState$ = this.store.select(selectActivityStateV2);
  isLoading$ = this.loaderService.response('loading-admin-courses-curriculumV2');

  actionEvent(event: any) {
    //   this.initFields(true);
    //   if (event === 'accept') {
    //     firstValueFrom(this.courseDetail$).then((courseDetail) => {
    //       this.projectService.toStatePublished(courseDetail.project.id);
    //     });
    //   } else if (event === 'reject') {
    //     firstValueFrom(this.courseDetail$).then((courseDetail) => {
    //       this.projectService.toStateRejected(courseDetail.project.id);
    //     });
    //   } else if (event === 'edit') {
    //     this.courseState = '1';
    //     this.readOnly = false;
    //   } else if (event === 'cancel') {
    //     this.courseState = '0';
    //     this.readOnly = true;
    //   }
    //   console.log('🚀  ~ actionEvent ~ this.readOnly:', this.readOnly);
  }

  onScrollDiv(event: Event): void {
    //   const target = event.target as HTMLElement;
    //   const scrollPosition = target.scrollTop + target.clientHeight;
    //   const scrollHeight = target.scrollHeight;
    //   this.showLoadMore = scrollPosition >= scrollHeight - 100;
  }
}
